import { useEffect, useMemo, useState } from "react";
import { useDispatch } from "react-redux";
import Select from "react-select";
import Modal from "react-bootstrap/Modal";
import toast from "react-hot-toast";

import SuccessSvg from "../../assets/images/pngs/success.png";
import FleetService from "../../utils/services/fleet.service";
import { Loading } from "../../utils/helpers/constants";
import { getUserWallet, getUserWalletEntries } from "../../store/actions/wallet.action";
import { swiftPayTableConfig } from "../../utils/helpers/dataTableConfigs";
import { paginationSize } from "../../utils/helpers/objectHelpers";
import PaginationTable from "../dataTable/PaginationTable";
import moment from "moment";
import { formatAmountToString } from "../../utils/helpers/logicHelper";

const SwiftPayModal = ({ onchange, profile }) => {

    const dispatch = useDispatch();
    const [show, setShow] = useState(true);
    const [pageData, setPageData] = useState([]);
    const [page, setPage] = useState(1);
    const [count, setCount] = useState(0);
    const [pageSize, setPageSize] = useState(paginationSize[1]);
    const [loading, setLoading] = useState(false);
    const [swiftPayTrips, setSwiftPayTrips] = useState([]);
    const [loadingSwiftPayTrips, setLoadingSwiftPayTrips] = useState(false);
    const [loadingPagination, setLoadingPagination] = useState(false);
    const [showSuccess, setShowSuccess] = useState(false);
   
    const [tab, setTab] = useState('create');
    // const [email, setEmail] = useState("");
    const [selectedTripOption, setSelectedTripOption] = useState(null);
    const [waybill, setWaybill] = useState("");
    const [files, setFiles] = useState([]);
    const [filterInput, setFilterInput] = useState("");
    // const [assignedBusiness, setAssignedBusiness] = useState(profile?.driver?.assigned_business?.reference ?? "");

    const tableObject = [...swiftPayTableConfig];
    const columns = useMemo(() => tableObject, []);

    const getTripAmount = (trip) => {
        if (!trip) return "";
        const amountValue =   trip.price ?? "-";
        if (amountValue === null || amountValue === undefined) return "";
        return formatAmountToString(amountValue);
    };

    const tripOptions = useMemo(() => {
        if (!Array.isArray(swiftPayTrips)) return [];
        return swiftPayTrips.map((trip) => {
            const reference = trip?.reference || trip?.id || "Unknown";
            const sourceCity = trip?.source?.city || "Unknown";
            const destinationCity = trip?.destination?.city || "Unknown";
            const amountValue = getTripAmount(trip);
            const amountLabel = amountValue ? `NGN ${amountValue}` : "Amount N/A";
            const paymentDate = trip.due_date ? `Trip payment date(${moment(trip.due_date).utc().format("Do [of] MMMM YYYY")})` : "Amount N/A";
            return {
                value: reference,
                label: `${reference} - ${sourceCity} to ${destinationCity} - ${amountLabel} - ${paymentDate}`,
                trip,
            };
        });
    }, [swiftPayTrips]);

    const selectedTrip = selectedTripOption ? selectedTripOption.trip : null;
    // const selectedAmount = getTripAmount(selectedTrip);

    const fetchDriverSwiftPayTrips = async () => {
        setLoadingSwiftPayTrips(true);

        const { data: responseData } = await FleetService.getDriverSwiftPayTrips();

        if (responseData.status === Loading.SUCCESS) {
            setSwiftPayTrips(responseData.data || []);
        } else {
            setSwiftPayTrips([]);
        }

        setLoadingSwiftPayTrips(false);
    };

    const fetchSwiftPayEntries = async (pageValue = 1, sizeValue = pageSize.value) => {
        setLoadingPagination(true);

        const pageIndex = pageValue - 1;
        const filter = `?size=${sizeValue}&page=${pageIndex}`;

        const { data: responseData } = await FleetService.getSwiftPayRequest(filter);

        if (responseData.status === Loading.SUCCESS) {
            setPageData(responseData.data || []);
            setCount(responseData.count || 0);
            setPage(pageValue);
        } else {
            setPageData([]);
            setCount(0);
        }

        setLoadingPagination(false);
    };

    useEffect(() => {
        if (show && tab === "entries") {
            fetchSwiftPayEntries(1, pageSize.value);
        }
        if (show && tab === "create") {
            fetchDriverSwiftPayTrips();
        }
    }, [show, tab]);

    const fetchPagination = (option, value) => {
        setPageSize(option);
        fetchSwiftPayEntries(value, option.value);
    };


    const resetForm = () => {
        setSelectedTripOption(null);
        setWaybill("");
        setFiles([]);
    };

    const readFileAsBase64 = (file) => (
        new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onload = () => {
                const result = reader.result || "";
                const base64 = result.toString().split(",")[1] || "";
                resolve(base64);
            };
            reader.onerror = reject;
            reader.readAsDataURL(file);
        })
    );

    const submitRequest = async () => {
        setLoading(true);

        if (!profile?.driver?.assigned_business?.reference) {
            toast.error(
                "Quick Payment is only available to drivers assigned to a business."
            );
            return;
        }

        if (!selectedTrip) {
            toast.error("Please select a Quick Pay trip");
            return;
        }

        // if (!waybill) {
        //     toast.error("Please enter waybill number");
        //     return;
        // }

        // if (!files || files.length === 0) {
        //     toast.error("Please select at least one file to upload");
        //     return;
        // }

        try {
            if (!selectedTrip.reference) {
                toast.error("Selected trip is missing a reference");
                return;
            }

            const amountValue = getTripAmount(selectedTrip);
            if (!amountValue) {
                toast.error("Selected trip has no amount");
                return;
            }

            const waybills = await Promise.all(
                files.map(async (file) => ({
                    base64File: await readFileAsBase64(file),
                    type: "WAYBILL",
                    number: waybill,
                }))
            );

            const payload = {
                email: profile.driver.user.email,
                trip_references: [selectedTrip.reference],
                amount: amountValue.split(",").join(""),
                business_reference: profile.driver.assigned_business.reference,
                waybills,
            };

            const { data: responseData } = await FleetService.createSwiftPayRequest(payload);
            if (responseData.status !== Loading.SUCCESS) {
                toast.error(
                    responseData?.message ||
                    "You cannot request for Quick Payment at the moment. Please try again later."
                );
            }
            else {
                dispatch(getUserWallet());
                dispatch(getUserWalletEntries(""));
                toast.success(responseData.message || "Quick Payment request created");
                // reset form
                resetForm();
                setShowSuccess(true);
            }
        } catch (err) {
            toast.error("Unable to upload documents");
        } finally {
            setLoading(false);
        }
    }


    const handleTripSelect = (option) => {
        setSelectedTripOption(option);
    };

    const handleFilesChange = (e) => {
        const selected = Array.from(e.target.files || []);
        if (selected.length === 0) return;

        setFiles(prev => {
            const combined = [...prev, ...selected];
            const map = new Map();
            combined.forEach(f => {
                const key = `${f.name}|${f.size}|${f.lastModified}`;
                if (!map.has(key)) map.set(key, f);
            });
            return Array.from(map.values());
        });
        e.target.value = null;
    }

    const removeFile = (index) => {
        setFiles(prev => prev.filter((_, i) => i !== index));
    }

    const handleClose = () => {
        setShow(false);
        onchange();
    };

    return (
        <>
            <Modal show={show} backdrop="static" keyboard={false} centered size="lg">
                <Modal.Body>
                    <div className="proceed">
                        <span id="close-modal-btn1" onClick={handleClose} className="close-modal">&times;</span>
                        <div className="d-flex gap-2 w-100 justify-content-center">
                            <button type="button" className={`btn border border-2 border-primary ${tab === 'create' ? 'btn-primary' : 'btn-outline-secondary'}`} onClick={() => setTab('create')}>Create Request</button>
                            <button type="button" className={`btn border border-2 border-primary ${tab === 'entries' ? 'btn-primary' : 'btn-outline-secondary'}`} onClick={() => setTab('entries')}>Quick Pay Entries</button>
                        </div>
                        {showSuccess ?
                            <div className="success-withdraw">
                                <div className="withdraw-field">
                                    <div className="opt-bg"><img src={SuccessSvg} alt="" /></div>
                                    <p>Successful!</p>
                                    <p id="success">Your Request is successful</p>
                                </div>
                                <div className="go-home"><span id="close-modal-btn-success1" onClick={handleClose}>Go back to home</span></div>
                            </div> :
                            <div className="withdraw-field">
                                {tab === 'create' &&
                                    <>
                                        <p className="text-lg">Request for Quick Payment</p>

                                        <div className="d-flex flex-column w-85 mt-4">
                                            <Select
                                                options={tripOptions}
                                                isClearable={false}
                                                isSearchable={true}
                                                maxMenuHeight={250}
                                                menuPlacement="bottom"
                                                placeholder="Select Trip"
                                                className="wd-100p"
                                                isLoading={loadingSwiftPayTrips}
                                                value={selectedTripOption}
                                                onChange={handleTripSelect}
                                            />
                                            {!loadingSwiftPayTrips && tripOptions.length === 0 && (
                                                <small className="text-muted mt-2">No Quick Pay trips available.</small>
                                            )}
                                        </div>

                                        {/* <div className="d-flex w-85 mt-3">
                                            <input
                                                className="form-control pd-y-22-f"
                                                value={selectedTrip?.reference || ""}
                                                type="text"
                                                placeholder="Account / Trip reference"
                                                readOnly
                                            />
                                        </div>

                                        <div className="d-flex w-85 mx-4 mt-3">
                                            <span className="input-group-text-left">NGN</span>
                                            <input
                                                type="text"
                                                className="form-control pd-y-22-f"
                                                value={selectedAmount}
                                                placeholder="Amount"
                                                readOnly
                                            />
                                        </div> */}

                                        {selectedTrip && (
                                            <>
                                            <div className="w-85 mt-3 d-flex">
                                                <p className="mb-1 text-muted">Trip amount:</p>
                                                <p className="mb-0 ms-2">NGN {formatAmountToString(selectedTrip.price) || "0"}</p>
                                            </div>
                                            <div className="w-85 mt-3 d-flex">
                                                <p className="mb-1 text-muted">Transaction amount:</p>
                                                <p className="mb-0 ms-2">NGN {formatAmountToString(selectedTrip.transaction_amount) || "0"}</p>
                                            </div>
                                            <div className="w-85 mt-3 d-flex">
                                                <p className="mb-1 text-muted">Approved amount:</p>
                                                <p className="mb-0 fw-bold ms-2">NGN {formatAmountToString(selectedTrip.approved_amount) || "0"}</p>
                                            </div>
                                            </>
                                        )}

                                        <div className="d-flex flex-column w-85 mt-3">
                                            <label htmlFor="swift-waybill-number" className="form-label mb-1">Waybill number</label>
                                            <input
                                                id="swift-waybill-number"
                                                className="form-control"
                                                value={waybill}
                                                onChange={e => setWaybill(e.target.value)}
                                                type="text"
                                                placeholder="Enter waybill number"
                                            />
                                        </div>

                                        <div className="d-flex w-85 mt-2 align-items-center">
                                            <input id="swift-waybill-files" type="file" accept="image/*" multiple onChange={handleFilesChange} style={{ display: 'none' }} />
                                            <label htmlFor="swift-waybill-files" className="btn btn-secondary btn-sm mb-0 mt-2">
                                                Upload waybill
                                            </label>
                                        </div>

                                        {files && files.length > 0 &&
                                            <div className="w-85 mt-2">
                                                <p className="mb-1"><strong>Selected files ({files.length}):</strong></p>
                                                <ul className="list-unstyled small">
                                                    {files.map((f, idx) => (
                                                        <span key={idx} className="badge bg-light border text-muted mb-2">
                                                            {f.name} <button type="button" className="btn btn-sm  p-0 ms-1 text-danger" onClick={() => removeFile(idx)}>x</button>
                                                        </span>
                                                    ))}
                                                </ul>
                                            </div>
                                        }

                                        {!profile?.driver?.assigned_business?.reference && (
                                            <small className="text-danger mt-2 d-block">
                                                You must be assigned to a business to use Quick Pay
                                            </small>
                                        )}
                                        <div className="d-flex gap-2 mt-4 w-85">
                                            <button onClick={handleClose} type="button" className="btn btn-outline-secondary">
                                                Cancel
                                            </button>
                                            <button onClick={submitRequest} disabled={!selectedTrip || !profile?.driver?.assigned_business?.reference || loading} className="btn main-btn">
                                                Create Request
                                                {loading && <>&nbsp;<i className="zmdi zmdi-spinner ms-2 mg-l-1 zmdi-hc-spin" /></>}
                                            </button>
                                        </div>
                                    </>
                                }


                                {tab === 'entries' &&
                                    <div className="w-85 mt-3">
                                        <div className="mt-2">
                                            <div className="input-group mb-2 wd-40p">
                                                <input className="form-control" value={filterInput} onChange={e => setFilterInput(e.target.value)} type="text"
                                                    placeholder="Search transactions..." />
                                            </div>
                                            <div className="w-100 table-md-bod overflow-x-auto">
                                                {loadingPagination ?
                                                    <div className="text-center py-4">
                                                        <i className="zmdi zmdi-spinner zmdi-hc-spin fs-3" />
                                                        <p className="mt-2 mb-0">Loading records...</p>
                                                    </div>
                                                    :
                                                    <PaginationTable columns={columns} filterData={filterInput} size={pageSize.value} count={count}
                                                        loading={loadingPagination} index={page} data={pageData}
                                                        setPage={value => fetchPagination(pageSize, value)} handleClick={() => false} />
                                                }
                                            </div>
                                        </div>
                                    </div>
                                }
                            </div>
                        }
                    </div>
                </Modal.Body>
            </Modal>
        </>
    );
};

export default SwiftPayModal;
